import {
  SdkLanguageType,
  KameleoonCore,
  VisitorCodeManager,
  IVisitorCodeManager,
  SetDataCallbackParametersType,
  Validator,
  KameleoonLogger,
  TargetingDataType,
  DataInfoType,
  KameleoonStorageKey,
} from '@kameleoon/javascript-sdk-core/browser';
import { DataStorage as SDKCoreDataStorage } from '@kameleoon/javascript-sdk-core/browser';
import { Requester } from './requester';
import { DataStorage } from './storage';
import { KameleoonEventSource } from './eventSource';
import { KameleoonVisitorCodeManager } from './visitorCodeManager';
import { IExternalVisitorCodeManager, SDKParameters } from './types';
import { KameleoonPRNG } from './prng';
import { DefaultLogger } from 'src/defaultLogger';
import { VisitorActivityManager } from 'src/visitorActivity';
import { ACTIVITY_INTERVAL } from 'src/constants';
import { ClientDataManager } from 'src/dataManager';
import { PlatformAnalyzer } from 'src/utils/platformAnalyzer';

/**
 * @class
 * KameleoonClient - a class for creating kameleoon client instance
 * */
export class KameleoonClient extends KameleoonCore {
  private domain?: string;
  private internalVisitorCodeManager: IVisitorCodeManager;
  private externalVisitorCodeManager: IExternalVisitorCodeManager;
  private customDataManager?: ClientDataManager;

  constructor({
    siteCode,
    configuration,
    externals,
    _internals,
    stubMode,
  }: SDKParameters) {
    if (stubMode) {
      super({
        siteCode,
        internalConfiguration: {} as any,
        stubMode,
      });
      this.internalVisitorCodeManager = {} as any;
      this.externalVisitorCodeManager = {} as any;
      return;
    }
    const prng = _internals?.prng || new KameleoonPRNG();
    const storage = externals?.storage || new DataStorage();
    const requester = externals?.requester || new Requester();
    const eventSource = externals?.eventSource || new KameleoonEventSource();
    const visitorCodeManager = new VisitorCodeManager();
    const externalVisitorCodeManager =
      externals?.visitorCodeManager || new KameleoonVisitorCodeManager();

    const packageInfo = _internals?.packageInfo || {
      type: SdkLanguageType.JAVASCRIPT,
      version: process.env.npm_package_version as string,
      isServer: false,
    };

    const targetingDataStorage = new SDKCoreDataStorage<TargetingDataType>(
      storage,
      KameleoonStorageKey.TargetingData,
    );
    const dataInfoStorage = new SDKCoreDataStorage<DataInfoType>(
      storage,
      KameleoonStorageKey.DataInfo,
    );

    const customDataManager = new ClientDataManager({
      dataStorage: targetingDataStorage,
      infoStorage: dataInfoStorage,
      cleanupInterval: 0,
      packageInfo: packageInfo,
      platformAnalyzer: _internals?.platformAnalyzer ?? new PlatformAnalyzer(),
    });

    const dependencies = {
      externalPRNG: prng,
      externalStorage: storage,
      externalRequester: requester,
      externalEventSource: eventSource,
      externalVisitorCodeManager: visitorCodeManager,
      externalPackageInfo: packageInfo,
      externalLogger: externals?.logger || new DefaultLogger(),
      customDataManager: customDataManager,
    };

    const settings = {
      useAbortController: true,
    };

    super({
      siteCode,
      configuration,
      internalConfiguration: {
        externals: dependencies,
        settings,
      },
    });

    const cookieDomain = Validator.validateDomain(configuration?.cookieDomain);
    this.domain = cookieDomain.ok ? cookieDomain.data : undefined;
    this.internalVisitorCodeManager = visitorCodeManager;
    this.externalVisitorCodeManager = externalVisitorCodeManager;
    this.customDataManager = customDataManager;
  }

  /**
   * @method initialize - an asynchronous method for KameleoonClient initialization by fetching Kameleoon SDK related data from server or by retrieving data from local source if data is up-to-date or update interval has not been reached
   * @returns {Promise<boolean>} Promise resolved into boolean field indicating success or fail
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.StorageWrite` Couldn't update storage data
   * - `KameleoonException.StorageRead` - Couldn't read storage data
   * - `KameleoonException.ClientConfiguration` Couldn't retrieve client configuration from Kameleoon Api
   * - `KameleoonException.MaximumRetriesReached` Maximum retries reached, request failed
   */
  public async initialize(): Promise<boolean> {
    if (this.stubMode) {
      return false;
    }
    const initialized = super.initialize();
    new VisitorActivityManager(
      this.customDataManager!,
      this.tracker,
      (visitorCode: string) => this._isConsentProvided(visitorCode),
      ACTIVITY_INTERVAL,
    );
    return initialized;
  }

  /**
   * @method getVisitorCode - obtains visitor code from the browser cookie, if the visitor code doesn't yet exist generates a random visitor code (or uses a provided default) and sets a new visitor code to cookie
   * @param {string | undefined} defaultVisitorCode - visitor code to be used in case there is no visitor code in cookies *Note*: if not passed visitor code will be randomly generated
   * @returns {string} result visitor code
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` - The visitor code is empty
   */
  public getVisitorCode(defaultVisitorCode?: string): string {
    if (this.stubMode) {
      return '';
    }
    KameleoonLogger.info`CALL KamaleoonClient.getVisitorCode(defaultVisitorCode: ${defaultVisitorCode})`;
    this.internalVisitorCodeManager.getData = (key: string) =>
      this.externalVisitorCodeManager.getData(key);

    this.internalVisitorCodeManager.setData = (
      parameters: SetDataCallbackParametersType,
    ) => {
      this.externalVisitorCodeManager.setData({
        ...parameters,
        domain: this.domain,
      });
    };

    const visitorCode =
      this.internalVisitorCodeManager.getVisitorCode(defaultVisitorCode);
    if (this.initialized) {
      this.customDataManager?.checkVisitor(visitorCode);
    }
    KameleoonLogger.info`RETURN KamaleoonClient.getVisitorCode(defaultVisitorCode: ${defaultVisitorCode}) -> (visitorCode: ${visitorCode})`;
    return visitorCode;
  }

  /**
   * @method setLegalConsent - sets or updates the legal consent status for a visitor identified by their unique visitor code, affecting values in the response cookies based on the consent status
   * @param {string} visitorCode - unique visitor identification string, can't exceed 255 characters length
   * @param {boolean} consent - a value representing the legal consent status. - 'true' indicates the
   *        visitor has given legal consent. - 'false' indicates the visitor has withdrawn or not
   *        provided legal consent.
   * @returns {void}
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` - The visitor code is empty
   */
  public setLegalConsent(visitorCode: string, consent: boolean): void {
    if (this.stubMode) {
      return;
    }
    super.setUserConsent({
      visitorCode,
      consent,
      setData: (parameters: SetDataCallbackParametersType): void => {
        this.externalVisitorCodeManager.setData({
          ...parameters,
          domain: this.domain,
        });
      },
    });
    super.flush(visitorCode);
  }
}
